import type { DisplayImage, MultimediaItem } from "../types";
import {
  CustomDisplayListItem,
  DisplayCondition,
  DisplayEntity,
  DisplayFormType,
  DisplayTranslation,
  LocaleCode,
} from "../types";
import { parseYoutubeId } from "../utils";

// ---------- helpers VIDEO ----------
function extractYoutubeURLFromHtml(html: string): string | undefined {
  if (!html) return undefined;
  const match = html.match(/<!--VIDEO_DISPLAY_URL\s+(.*?)\s+-->/);
  return match ? match[1] : undefined;
}

function extractDescriptionFromVideoHtml(html: string): string {
  if (!html) return "";
  const startMarker = "<!--DESCRIPTION_CONTENT_START-->";
  const endMarker = "<!--DESCRIPTION_CONTENT_END-->";
  const start = html.indexOf(startMarker);
  const end = html.lastIndexOf(endMarker);

  if (start === -1 || end === -1) return "";

  const content = html.substring(start + startMarker.length, end);
  // @ts-ignore
  const divMatch = content.match(/<div>(.*)<\/div>/s);
  return divMatch ? divMatch[1].trim() : content.trim();
}

function buildYoutubeHtml(url: string, label: string, description: string): string {
  const videoId = parseYoutubeId(url);
  if (!videoId) return description || "";

  return `<!--VIDEO_DISPLAY_URL ${url} -->
    <span contenteditable="false" draggable="true">
      <iframe width="100%" height="315"
        src='https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&modestbranding=1&rel=0'
        frameborder="0" allowFullScreen="allowFullScreen">
      </iframe>
    </span>
    <!--TITLE_CONTENT_START-->
    <div style="color: rgb(34,34,34); font-size: 20px; font-weight: 600;
                text-transform: uppercase; text-align: left;
                margin-top: 15px; margin-bottom: 10px;">
      ${label}
    </div>
    <!--TITLE_CONTENT_END-->
    <!--DESCRIPTION_CONTENT_START-->
    <div>${description || ""}</div>
    <!--DESCRIPTION_CONTENT_END-->`;
}

// Helper: MultimediaItem -> DisplayImage
function multimediaToImage(item: MultimediaItem): DisplayImage {
  return Object.assign(new File([""], item.filename), {
    id: item.id,
    url: item.url ?? "",
  });
}

// Helper: DisplayImage -> MultimediaItem
function imageToMultimedia(item: DisplayImage): MultimediaItem {
  return {
    id: item.id,
    filename: item.name,
    url: item.url,
    file: item,
  };
}

// ---------- entity -> form ----------
export const entityToFormValues = (entity?: Partial<DisplayEntity>): DisplayFormType => {
  const e = entity ?? {};
  const tickerFilterCondition = e.conditions?.find(c => c.type === "TICKER");
  const tickerFilter = tickerFilterCondition ? tickerFilterCondition.value.join(",") : "";

  let contentType: "HTML" | "VIDEO" | "IMAGE" = "HTML";
  let youTubeURL: string | undefined;

  if (e.multimedia && e.multimedia.length > 0) {
    contentType = "IMAGE";
  } else if (e.translations?.some(t => t.content?.includes("VIDEO_DISPLAY_URL"))) {
    contentType = "VIDEO";
    youTubeURL = extractYoutubeURLFromHtml(e.translations[0].content || "");
  }

  return {
    id: e.id ?? "",
    propertyTicker: e.propertyTicker ?? "",
    active: e.active ?? false,
    defaultOption: e.defaultOption ?? false,
    position: e.position,
    ticker: e.ticker ?? "",
    type: e.type ?? "CUSTOM",
    contentType,
    entityFilter:
      (e.conditions?.find(c => c.type === "ENTITY")?.value[0] as "PROPERTY" | "ACCOMMODATION") ??
      "PROPERTY",
    translationsList: (e.translations || []).map(translation => ({
      id: translation.id,
      locale: translation.locale,
      name: translation.label ?? "",
      description:
        contentType === "VIDEO"
          ? extractDescriptionFromVideoHtml(translation.content ?? "")
          : translation.content ?? "",
    })),
    multimedia: e.multimedia ?? [],
    conditions: e.conditions ?? [],
    startDate: e.startDate ?? "",
    endDate: e.endDate ?? "",
    youTubeURL,
    tickerFilter,
    imageManager: Array.isArray(e.multimedia) ? e.multimedia.map(multimediaToImage) : [],
    originalMultimedia: e.multimedia ?? [],
  };
};

// ---------- form -> dto ----------
export const formValuesToDto = (values: DisplayFormType): DisplayEntity => {
  const {
    youTubeURL,
    tickerFilter,
    translationsList,
    contentType,
    entityFilter,
    imageManager,
    originalMultimedia,
    travelWindow,
    ...rest
  } = values;

  const conditions: DisplayCondition[] = [];
  if (entityFilter) {
    conditions.push({
      class: "stringComparison",
      value: [entityFilter],
      type: "ENTITY",
      modifier: "INCLUDE",
    });
  }
  if (tickerFilter && entityFilter === "ACCOMMODATION") {
    const tickers = tickerFilter
      .split(",")
      .map(t => t.trim())
      .filter(Boolean);
    if (tickers.length) {
      conditions.push({
        class: "stringComparison",
        value: tickers,
        type: "TICKER",
        modifier: "INCLUDE",
      });
    }
  }

  const translations: DisplayTranslation[] = (translationsList || []).map(translation => {
    const label = translation.name ?? "";
    const description = translation.description ?? "";

    const builtHtml =
      contentType === "VIDEO" && youTubeURL
        ? buildYoutubeHtml(youTubeURL, label, description)
        : description;

    const resultTranslation: DisplayTranslation = {
      locale: translation.locale as LocaleCode,
      label: label,
      content: builtHtml,
    };

    // Only assign id if it exists on translation
    if ("id" in translation && typeof translation.id === "string") {
      resultTranslation.id = translation.id;
    }

    return resultTranslation;
  });

  const result: DisplayEntity = {
    ...rest,
    conditions,
    translations,
  };

  if (contentType === "IMAGE" && imageManager) {
    result.multimedia = imageManager.map(imageToMultimedia);
  } else if (contentType === "IMAGE") {
    result.multimedia = originalMultimedia || [];
  }

  if (rest.id && rest.id !== "" && rest.id !== " ") {
    result.id = rest.id;
  }

  return result;
};

// ---------- entity -> list ----------
export const mapToObjectFromHttp = (entity: DisplayEntity): CustomDisplayListItem => {
  if (!entity.id) throw new Error("Entity must have an ID for list display");

  return {
    id: entity.id,
    active: entity.active,
    ticker: entity.ticker,
    translations: entity.translations,
    startDate: entity.startDate || "",
    endDate: entity.endDate || "",
    location:
      (entity.conditions?.find(c => c.type === "ENTITY")?.value[0] as
        | "PROPERTY"
        | "ACCOMMODATION") ?? "PROPERTY",
    showFor: entity.conditions?.find(c => c.type === "TICKER")?.value || [],
    contentType: entity.contentType,
    position: typeof entity.position === "number" ? entity.position : 0,
  };
};
